// ── oddsBoard — what GameDetail's OddsPapi table shows ──────────────
//
// The table at the bottom of GameDetail lists every book OddsPapi carries
// for ONE market: the consensus fair value first, then Pinnacle, then the
// field books. It follows the line the trade panel is on, and shows the
// full-game moneyline when nothing else is selected.
//
// Everything here is pure: which market the selection names, which slice
// carries it, and how its sides and books lay out. Values render verbatim
// from the wire (rule 6c — g1 never computes fair values); the books'
// implied percentages are their quoted, vigged prices.

import { alignOpSidesToRow, OP_PERIOD_WIRE, opTeamTotalTeamCode, type OpSideQuote } from '@/lib/opOdds';
import type { OpOddsSlice } from '@/stores/marketStore';
import type { HydraOpBookQuote } from '@/clients/HydraClient';
import type { GameRow, OpBucketRef, TradeTicketContext } from '../ValueDashboard/types';
import type { ActiveSpreadTotal } from './SpreadTotalSection';

/** The market the table follows. `spread`/`total` are FULL-GAME lines named
 *  the way the Spread/Total section names them (spread: signed from the away
 *  team's perspective); `bucket` is an exact OddsPapi bucket a Game Lines row
 *  pinned; `unmapped` is a selection with no OddsPapi counterpart (a US
 *  contract, a knockout To Advance market). */
export type OddsBoardSelection =
  | { kind: 'moneyline' }
  | { kind: 'spread'; line: number }
  | { kind: 'total'; line: number }
  | { kind: 'bucket'; bucket: OpBucketRef }
  | { kind: 'unmapped' };

/**
 * The selected line, by the same precedence the trade panel resolves its game
 * (GameDetail's livePanelGame): a Game Lines line or ladder, then a full-game
 * spread/total, then the ticket's own row. With no ticket, and on the
 * page's moneyline seed, it is the full-game moneyline.
 */
export function resolveOddsBoardSelection(input: {
  /** Row keys that ARE the full-game moneyline (the page's game row). */
  moneylineKeys: readonly string[];
  gameLinesActive: Pick<ActiveSpreadTotal, 'game' | 'opBucket'> | null;
  activeSpreadTotal: Pick<ActiveSpreadTotal, 'game'> | null;
  tradeCtx: Pick<TradeTicketContext, 'game' | 'opBucket'> | null;
}): OddsBoardSelection {
  const { gameLinesActive, activeSpreadTotal, tradeCtx } = input;
  if (gameLinesActive) {
    return gameLinesActive.opBucket ? { kind: 'bucket', bucket: gameLinesActive.opBucket } : { kind: 'unmapped' };
  }
  // The Spread/Total section is always full game.
  if (activeSpreadTotal) return fullGameLine(activeSpreadTotal.game);
  if (!tradeCtx) return { kind: 'moneyline' };
  if (tradeCtx.opBucket) return { kind: 'bucket', bucket: tradeCtx.opBucket };
  const key = tradeCtx.game.key;
  // Soccer's 1X2 outcomes trade through per-outcome synths of the same game.
  if (input.moneylineKeys.includes(key) || key.includes('|multi|')) return { kind: 'moneyline' };
  return { kind: 'unmapped' };
}

function fullGameLine(game: GameRow): OddsBoardSelection {
  if (game.spreadOutcome) return { kind: 'spread', line: game.spreadOutcome.line };
  const total = game.totalOutcome;
  if (total && (total.period == null || total.period === 'full')) return { kind: 'total', line: total.line };
  return { kind: 'unmapped' };
}

export interface OddsBoardReaders {
  moneyline: (game: string) => OpOddsSlice | null;
  spread: (game: string, home: string, line: number) => { slice: OpOddsSlice; swapped: boolean } | null;
  total: (game: string, line: number) => OpOddsSlice | null;
  bucket: (
    game: string,
    periodKey: number | string,
    marketType: string,
    points?: number | null,
    prop?: { stat: string; playerKey: string } | null,
  ) => OpOddsSlice | null;
}

/** The slice for a selection — exact buckets only; a line OddsPapi does not
 *  carry is null, never a neighbouring line or another market. */
export function resolveOddsBoardSlice(
  selection: OddsBoardSelection,
  game: { key: string; homeCode: string },
  readers: OddsBoardReaders,
): OpOddsSlice | null {
  switch (selection.kind) {
    case 'moneyline':
      return readers.moneyline(game.key);
    case 'spread':
      return readers.spread(game.key, game.homeCode, selection.line)?.slice ?? null;
    case 'total':
      return readers.total(game.key, selection.line);
    case 'bucket': {
      const b = selection.bucket;
      return readers.bucket(b.gameKey, b.periodKey, b.marketType, b.points, b.prop);
    }
    case 'unmapped':
      return null;
  }
}

const FAMILY_LABELS: Record<string, string> = {
  moneyline: 'Moneyline',
  spread: 'Spread',
  total: 'Total',
  'teamtotal-home': 'Team Total',
  'teamtotal-away': 'Team Total',
  'total-touchdowns': 'Total Touchdowns',
  'total-fieldgoals': 'Total Field Goals',
  oddeven: 'Odd / Even',
  'oddeven-away': 'Odd / Even — Away',
  'oddeven-home': 'Odd / Even — Home',
  'player-prop': 'Player Prop',
};

const signedLine = (n: number) => (n > 0 ? `+${n}` : `${n}`);

/** g1's period name for a vendor period string (OP_PERIOD_WIRE reversed). */
const PERIOD_NAMES = new Map(Object.entries(OP_PERIOD_WIRE).map(([name, wire]) => [wire, name]));

/** Card title for a selection, readable before any slice arrives. */
export function oddsBoardTitle(selection: OddsBoardSelection, game: { awayCode: string }, slice: OpOddsSlice | null): string {
  switch (selection.kind) {
    case 'moneyline':
      return 'Moneyline';
    case 'spread':
      return `Spread ${game.awayCode} ${signedLine(selection.line)}`;
    case 'total':
      return `Total ${selection.line}`;
    case 'bucket': {
      const b = selection.bucket;
      const family = FAMILY_LABELS[b.marketType] ?? b.marketType;
      let detail = '';
      if (b.marketType === 'player-prop') {
        detail = ` ${slice?.market.player ?? ''} ${b.prop?.stat ?? ''}`.trimEnd();
      } else if (b.marketType === 'spread' && b.points != null) {
        // Wire points are the provider home's handicap.
        detail = slice ? ` ${slice.homeTeam} ${signedLine(b.points)}` : ` ${signedLine(b.points)}`;
      } else if (slice != null && opTeamTotalTeamCode(slice) != null) {
        detail = ` ${opTeamTotalTeamCode(slice)}`;
      }
      const line = b.points != null && b.marketType !== 'spread' ? ` ${b.points}` : '';
      const period = b.periodKey === 1 ? '' : ` · ${PERIOD_NAMES.get(String(b.periodKey)) ?? b.periodKey}`;
      return `${family}${detail}${line}${period}`;
    }
    case 'unmapped':
      return 'Selected market';
  }
}

/** One row of the table: a side of the market, in page order. */
export interface OddsBoardSide {
  slot: 'away' | 'home' | 'draw';
  label: string;
  /** Consensus fair tenths for this side (null when the frame has none). */
  consensusTenths: number | null;
  /** Each book's quote for this side, by bookmaker slug. */
  quotes: Map<string, { quote: OpSideQuote; updatedAtMs: number }>;
}

/** Families whose sides are the two TEAMS — they follow the page's away/home
 *  order. Every other family keeps its slot order (Over/Odd on the away
 *  slot), which never swaps. */
const TEAM_SIDED = new Set(['moneyline', 'spread']);

/** The market's sides in page order with every book's quote aligned to them
 *  through the one OP alignment helper (alignOpSidesToRow). */
export function oddsBoardSides(slice: OpOddsSlice, game: { awayCode: string; homeCode: string }): OddsBoardSide[] {
  const market = slice.market;
  const teamSided = TEAM_SIDED.has(market.marketType);
  const rowHome = teamSided ? game.homeCode : null;
  const swapped = rowHome != null && slice.awayTeam === rowHome;
  const c = market.consensus;
  const away: OddsBoardSide = {
    slot: 'away',
    label: sideLabel(slice, 'away', swapped, game),
    consensusTenths: (swapped ? c?.homeFairProbTenths : c?.awayFairProbTenths) ?? null,
    quotes: new Map(),
  };
  const home: OddsBoardSide = {
    slot: 'home',
    label: sideLabel(slice, 'home', swapped, game),
    consensusTenths: (swapped ? c?.awayFairProbTenths : c?.homeFairProbTenths) ?? null,
    quotes: new Map(),
  };
  const draw: OddsBoardSide = { slot: 'draw', label: 'Draw', consensusTenths: c?.drawFairProbTenths ?? null, quotes: new Map() };
  for (const book of market.books) {
    const aligned = alignOpSidesToRow(slice, book, rowHome);
    away.quotes.set(book.bookmaker, { quote: aligned.away, updatedAtMs: book.updatedAtMs });
    home.quotes.set(book.bookmaker, { quote: aligned.home, updatedAtMs: book.updatedAtMs });
    if (aligned.draw) draw.quotes.set(book.bookmaker, { quote: aligned.draw, updatedAtMs: book.updatedAtMs });
  }
  const sides = [away, home];
  if (draw.consensusTenths != null || draw.quotes.size > 0) sides.push(draw);
  return sides;
}

function sideLabel(
  slice: OpOddsSlice,
  side: 'away' | 'home',
  swapped: boolean,
  game: { awayCode: string; homeCode: string },
): string {
  const m = slice.market;
  const points = m.points;
  switch (m.marketType) {
    case 'moneyline':
      return side === 'away' ? game.awayCode : game.homeCode;
    case 'spread': {
      if (points == null) return side === 'away' ? game.awayCode : game.homeCode;
      // Wire points = the PROVIDER home's handicap; the provider away takes
      // its negation. Swapped ⇒ the provider home is the page's away.
      const providerSide = swapped ? (side === 'away' ? 'home' : 'away') : side;
      const hcp = providerSide === 'home' ? points : -points;
      return `${side === 'away' ? game.awayCode : game.homeCode} ${signedLine(hcp)}`;
    }
    case 'oddeven':
    case 'oddeven-away':
    case 'oddeven-home':
      return side === 'away' ? 'Odd' : 'Even';
    default: {
      const ou = side === 'away' ? 'Over' : 'Under';
      const team = opTeamTotalTeamCode(slice);
      return `${team ? `${team} ` : ''}${ou}${points != null ? ` ${points}` : ''}`;
    }
  }
}

/** Display names for OddsPapi bookmaker slugs (hydracore consensusBooks.ts
 *  census). An unknown slug renders as itself. */
const BOOK_LABELS: Record<string, string> = {
  pinnacle: 'Pinnacle',
  fanduel: 'FanDuel',
  draftkings: 'DraftKings',
  betmgm: 'BetMGM',
  caesars: 'Caesars',
  bet365: 'bet365',
  betrivers: 'BetRivers',
  hardrockbet: 'Hard Rock',
  circasports: 'Circa',
  ballybet: 'Bally Bet',
  betparx: 'betPARX',
  borgata: 'Borgata',
  fourwinds: 'Four Winds',
  desertdiamond: 'Desert Diamond',
  'bookmaker.eu': 'Bookmaker',
  'pointsbet.com.au': 'PointsBet AU',
  sbobet: 'SBOBET',
  singbet: 'SingBet',
  '3et': '3ET',
  '198bet': '198Bet',
  kaiyun: 'Kaiyun',
};

export function oddsBookLabel(slug: string): string {
  return BOOK_LABELS[slug] ?? slug;
}

/** First-layout order for the field books: the US majors, then the rest of
 *  the census. A book outside this list sorts after it by slug. */
const BOOK_ORDER = [
  'fanduel', 'draftkings', 'betmgm', 'caesars', 'bet365', 'betrivers',
  'hardrockbet', 'circasports', 'ballybet', 'betparx', 'borgata', 'fourwinds',
  'desertdiamond', 'bookmaker.eu', 'pointsbet.com.au', 'sbobet', 'singbet', '3et',
  '198bet', 'kaiyun',
];
const bookRank = (slug: string) => {
  const i = BOOK_ORDER.indexOf(slug);
  return i < 0 ? BOOK_ORDER.length : i;
};

/** Field-book columns after Pinnacle (Dan, 2026-10-10: the ten most recent). */
export const FIELD_BOOK_CAP = 10;
/** A shown field book gives up its column only after this long without an
 *  update, so live books keep their columns and only a quiet one makes room —
 *  without it, twenty books ticking in a live game would swap columns every
 *  second. */
export const FIELD_BOOK_EVICT_MS = 30_000;

/**
 * The field-book columns, which never move sideways. Pinnacle is not among
 * them; it keeps its own column always.
 *
 * Each book keeps the slot it has. A book that is not shown takes, newest
 * first: a slot its book has left (no quote and no withdrawal entry), else a
 * new slot at the end while under the cap, else the slot of the stalest shown
 * book that has gone FIELD_BOOK_EVICT_MS without an update and is older than
 * it. A slot nobody fills closes up. A withdrawn book (rule 12) counts as
 * never updated: it is shown only while there is room. The first layout is
 * the newest FIELD_BOOK_CAP books in BOOK_ORDER.
 */
export function nextFieldBookSlots(
  prev: readonly string[],
  market: { books: HydraOpBookQuote[]; withdrawnBooks?: { bookmaker: string }[] },
  nowMs: number,
  cap: number = FIELD_BOOK_CAP,
): string[] {
  const at = new Map<string, number>();
  for (const b of market.books) if (b.bookmaker !== 'pinnacle') at.set(b.bookmaker, b.updatedAtMs);
  for (const w of market.withdrawnBooks ?? []) {
    if (w.bookmaker !== 'pinnacle' && !at.has(w.bookmaker)) at.set(w.bookmaker, -Infinity);
  }
  const time = (slug: string) => at.get(slug) ?? -Infinity;
  const ranked = [...at.keys()].sort((a, b) =>
    time(a) !== time(b) ? (time(b) > time(a) ? 1 : -1) : bookRank(a) - bookRank(b) || a.localeCompare(b));
  if (prev.length === 0) {
    return ranked.slice(0, cap).sort((a, b) => bookRank(a) - bookRank(b) || a.localeCompare(b));
  }
  const slots: (string | null)[] = prev.map((slug) => (at.has(slug) ? slug : null));
  const shown = new Set(slots.filter((slug): slug is string => slug !== null));
  for (const slug of ranked) {
    if (shown.has(slug)) continue;
    let i = slots.indexOf(null);
    if (i < 0 && slots.length < cap) i = slots.push(null) - 1;
    if (i < 0) {
      for (let j = 0; j < slots.length; j++) {
        const t = time(slots[j]!);
        if (nowMs - t >= FIELD_BOOK_EVICT_MS && t < time(slug) && (i < 0 || t < time(slots[i]!))) i = j;
      }
      // Newest first: no older candidate can evict what this one could not.
      if (i < 0) break;
      shown.delete(slots[i]!);
    }
    slots[i] = slug;
    shown.add(slug);
  }
  return slots.filter((slug): slug is string => slug !== null);
}

/** A book column's last-update clock: by the second up to 5 s, then 5s+,
 *  10s+, 20s+, 30s+ and 60s+. Past five seconds the text changes only at a
 *  bucket edge, so the shared one-second clock rarely touches it. A quote
 *  stamped ahead of our clock reads 1s. */
export function quoteAgeLabel(updatedAtMs: number, nowMs: number): string {
  const age = nowMs - updatedAtMs;
  if (age <= 5_000) return `${Math.max(1, Math.ceil(age / 1_000))}s`;
  if (age < 10_000) return '5s+';
  if (age < 20_000) return '10s+';
  if (age < 30_000) return '20s+';
  if (age < 60_000) return '30s+';
  return '60s+';
}
